import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import Swal from "sweetalert2";
import { Modal } from "react-bootstrap";
import axiosInstance from "../../axiosInstance";
import AdLayout from "../../components/AdLayout";
import CustomerAsyncSelect from "../../components/warehouse/CustomerAsyncSelect";
import Paging from "../../components/Paging";
import * as general from "../../libs/general";
import { fetchWarehousePermissions, WAREHOUSE_UNCLAIMED_FEATURE } from "../../libs/customerCreatePermission";
import "../../components/warehouse/warehouse.css";

const STATUS_META = {
  PENDING: { label: "Chưa xử lý", variant: "warning" },
  CREATED: { label: "Đã tạo đơn vô chủ", variant: "success" },
  IGNORED: { label: "Bỏ qua", variant: "secondary" },
};

const UNCLAIMED_CUSTOMER_CODE = "ZTO-VH00";

const STATUS_FILTERS = [
  { value: "PENDING", label: "Chưa xử lý" },
  { value: "CREATED", label: "Đã tạo đơn" },
  { value: "IGNORED", label: "Bỏ qua" },
  { value: "", label: "Tất cả" },
];

/** KHO VẬN → Đơn vô chủ: danh sách mã vận đơn chưa tồn tại khi quét nhập (BRD §4.3). */
const UnknownCodesPage = () => {
  const [status, setStatus] = useState("PENDING");
  const [keyword, setKeyword] = useState("");
  const [page, setPage] = useState(0);
  const [size, setSize] = useState(50);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState([]);
  const [perm, setPerm] = useState({ canCreate: false, canDelete: false });
  const [submitting, setSubmitting] = useState(false);
  // Hộp xác nhận tạo Đơn vô chủ: gán luôn Mã KH, hoặc để mặc định ZTO-VH00.
  const [createConfirm, setCreateConfirm] = useState(null);

  useEffect(() => {
    fetchWarehousePermissions(axiosInstance, WAREHOUSE_UNCLAIMED_FEATURE).then(setPerm);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get("/api/warehouse/unknownCodes", {
        params: { page, size, keyword: keyword.trim() || undefined, types: status || undefined },
      });
      setPageData(res.data?.page || null);
      setSelected([]);
    } catch (error) {
      toast.error(error.response?.data?.message || "Không tải được danh sách");
    } finally {
      setLoading(false);
    }
  }, [page, size, keyword, status]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, size, status]);

  const rows = pageData?.content || [];
  // Thao tác chỉ áp dụng cho mã "Chưa xử lý", ở tab "Chưa xử lý" hoặc "Tất cả":
  // Tạo đơn vô chủ, hoặc Xóa (chuyển sang Bỏ qua — không xử lý nữa).
  const actionTab = status === "PENDING" || status === "";
  const canCreate = actionTab && perm.canCreate;
  const canIgnore = actionTab && perm.canDelete;
  const selectableRows = canCreate || canIgnore ? rows.filter((row) => row.status === "PENDING") : [];
  const allSelected = selectableRows.length > 0 && selectableRows.every((row) => selected.includes(row.id));

  const toggleRow = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const toggleAll = () => setSelected(allSelected ? [] : selectableRows.map((row) => row.id));

  const handleCreate = async () => {
    if (selected.length === 0) {
      toast.warning("Chọn ít nhất một mã vận đơn");
      return;
    }
    const codes = rows.filter((row) => selected.includes(row.id)).map((row) => row.code);
    setCreateConfirm({ codes, assign: false, customer: null });
  };

  const submitCreate = async () => {
    const customerCode = createConfirm.assign ? createConfirm.customer?.value : "";
    if (createConfirm.assign && !customerCode) {
      toast.warning("Chọn khách hàng cần gán cho đơn");
      return;
    }

    setSubmitting(true);
    try {
      const res = await axiosInstance.post("/api/warehouse/unknownCodes/createOrders", { ids: selected, customerCode });
      if (res.data?.success) {
        toast.success(res.data.message);
        setCreateConfirm(null);
      } else {
        toast.error(res.data?.message || "Tạo Đơn vô chủ thất bại");
      }
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || "Tạo Đơn vô chủ thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (selected.length === 0) {
      toast.warning("Chọn ít nhất một mã vận đơn");
      return;
    }
    const codes = rows.filter((row) => selected.includes(row.id)).map((row) => row.code);
    const confirm = await Swal.fire({
      title: `Xóa ${selected.length} mã khỏi danh sách chờ?`,
      text: `Các mã sẽ chuyển sang tab "Bỏ qua" và không được tạo Đơn vô chủ nữa: ${codes.join(", ")}`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Xóa",
      confirmButtonColor: "#dc3545",
      cancelButtonText: "Hủy",
    });
    if (!confirm.isConfirmed) return;

    setSubmitting(true);
    try {
      const res = await axiosInstance.post("/api/warehouse/unknownCodes/ignore", { ids: selected });
      if (res.data?.success) toast.success(res.data.message);
      else toast.error(res.data?.message || "Xóa thất bại");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.message || "Xóa thất bại");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearch = (event) => {
    event.preventDefault();
    if (page === 0) load();
    else setPage(0);
  };

  return (
    <AdLayout>
      <div className="warehouse-page">
        <div className="warehouse-page__header">
          <h1 className="warehouse-page__title">Mã vận đơn chưa tồn tại / Đơn vô chủ</h1>
          <div className="d-flex gap-2">
            <Link className="btn btn-outline-secondary btn-sm" to="/kho-van/quet-nhap">
              Quét nhập kho VN
            </Link>
            {canCreate && (
              <button
                type="button"
                className="btn btn-outline-primary btn-sm"
                onClick={handleCreate}
                disabled={submitting || selected.length === 0}
              >
                Tạo đơn vô chủ{selected.length > 0 ? ` (${selected.length})` : ""}
              </button>
            )}
            {canIgnore && (
              <button
                type="button"
                className="btn btn-outline-danger btn-sm"
                onClick={handleDelete}
                disabled={submitting || selected.length === 0}
              >
                Xóa{selected.length > 0 ? ` (${selected.length})` : ""}
              </button>
            )}
          </div>
        </div>

        <div className="d-flex flex-wrap gap-2 mb-3">
          <div className="btn-group" role="group">
            {STATUS_FILTERS.map((item) => (
              <button
                key={item.value || "all"}
                type="button"
                className={`btn btn-sm ${status === item.value ? "btn-secondary" : "btn-outline-secondary"}`}
                onClick={() => {
                  setStatus(item.value);
                  setPage(0);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>
          <form className="d-flex gap-2" onSubmit={handleSearch}>
            <input
              className="form-control form-control-sm"
              placeholder="Tìm mã vận đơn"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
            />
            <button type="submit" className="btn btn-sm btn-outline-primary">Tìm</button>
          </form>
        </div>

        <div className="table-responsive warehouse-result-table" data-loading={loading}>
          <table className="table table-sm table-hover align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 36 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} disabled={selectableRows.length === 0} aria-label="Chọn tất cả" />
                </th>
                <th style={{ width: 50 }}>STT</th>
                <th>Mã vận đơn</th>
                <th>Thời gian quét</th>
                <th>Người quét</th>
                <th>Số lần quét</th>
                <th>Trạng thái xử lý</th>
                <th>Đơn đã tạo</th>
              </tr>
            </thead>
            <tbody>
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-center text-muted py-4">Không có mã vận đơn nào</td>
                </tr>
              )}
              {rows.map((row, index) => {
                const meta = STATUS_META[row.status] || { label: row.status, variant: "secondary" };
                const selectable = selectableRows.some((item) => item.id === row.id);
                return (
                  <tr key={row.id}>
                    <td>
                      <input type="checkbox" checked={selected.includes(row.id)} onChange={() => toggleRow(row.id)} disabled={!selectable} />
                    </td>
                    <td>{(pageData?.number || 0) * (pageData?.size || size) + index + 1}</td>
                    <td className="fw-semibold">{row.code}</td>
                    <td className="text-nowrap">{general.formatDateTime3(row.lastScannedAt)}</td>
                    <td>{row.scannedByName || "-"}</td>
                    <td>{row.scanCount}</td>
                    <td><span className={`badge bg-${meta.variant}`}>{meta.label}</span></td>
                    <td className="text-nowrap">
                      {row.createdOrderCode || "-"}
                      {row.processedByName && (
                        <div className="small text-muted">
                          {row.processedByName} · {general.formatDateTime3(row.processedAt)}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {pageData && pageData.totalPages > 0 && (
          <div className="mt-2">
            <Paging
              pageData={pageData}
              onPageChange={(number) => setPage(number - 1)}
              onItemsPerPageChange={(event) => {
                setSize(Number(event.target.value));
                setPage(0);
              }}
              isShowTotal
            />
          </div>
        )}
      </div>

      {createConfirm && (
        <Modal show centered onHide={() => !submitting && setCreateConfirm(null)}>
          <Modal.Header closeButton={!submitting}>
            <Modal.Title as="h5">Tạo {createConfirm.codes.length} Đơn vô chủ?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="mb-3">
              Đơn sẽ được tạo với trạng thái Kho VN: <span className="fw-semibold">{createConfirm.codes.join(", ")}</span>
            </p>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="unclaimed-customer-default"
                checked={!createConfirm.assign}
                disabled={submitting}
                onChange={() => setCreateConfirm({ ...createConfirm, assign: false })}
              />
              <label className="form-check-label" htmlFor="unclaimed-customer-default">
                Để mặc định khách {UNCLAIMED_CUSTOMER_CODE}
              </label>
            </div>
            <div className="form-check mb-2">
              <input
                className="form-check-input"
                type="radio"
                id="unclaimed-customer-assign"
                checked={createConfirm.assign}
                disabled={submitting}
                onChange={() => setCreateConfirm({ ...createConfirm, assign: true })}
              />
              <label className="form-check-label" htmlFor="unclaimed-customer-assign">
                Gán luôn mã khách hàng
              </label>
            </div>
            {createConfirm.assign && (
              <CustomerAsyncSelect
                value={createConfirm.customer}
                onChange={(option) => setCreateConfirm({ ...createConfirm, customer: option })}
                autoFocus
                isDisabled={submitting}
              />
            )}
          </Modal.Body>
          <Modal.Footer>
            <button type="button" className="btn btn-sm btn-secondary" onClick={() => setCreateConfirm(null)} disabled={submitting}>
              Hủy
            </button>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              onClick={submitCreate}
              disabled={submitting || (createConfirm.assign && !createConfirm.customer)}
            >
              Xác nhận tạo
            </button>
          </Modal.Footer>
        </Modal>
      )}
    </AdLayout>
  );
};

export default UnknownCodesPage;
